import { useCallback, useEffect, useRef, useState, type FormEvent, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { CheckCircle2, Mail, Send, X } from 'lucide-react'
import type { Lead } from '../../types/crm'
import { EmailComposer } from '../gmail/EmailComposer'
import { serializeEmail } from '../gmail/emailContent'
import {
  connectGmail,
  GMAIL_OAUTH_HOST_MESSAGE,
  GMAIL_REAUTHORIZATION_REQUIRED,
  GmailRequestError,
  loadGmailStatus,
  sendGmailEmail,
  type SendEmailResult,
} from '../gmail/gmailClient'
import { getLeadEmail } from './unifiedLeadFields'
import './SendEmailModal.css'

type ConnectionState = 'checking' | 'ready' | 'disconnected' | 'unavailable'

const RECONNECT_MESSAGE =
  'Your Gmail connection expired or was revoked. Reconnect Gmail to continue.'

/* Same rule the server applies: one plain address, no display name or list. */
const RECIPIENT_PATTERN = /^[^\s@<>,;"]+@[^\s@<>,;"]+\.[^\s@<>,;"]+$/

const IS_APPLE =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent)

export function SendEmailModal({ lead, onClose }: { lead: Lead; onClose: () => void }) {
  const [connection, setConnection] = useState<ConnectionState>('checking')
  const [to, setTo] = useState(() => getLeadEmail(lead))
  const [subject, setSubject] = useState('')
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState<SendEmailResult | null>(null)
  const [toast, setToast] = useState(false)
  const [error, setError] = useState('')
  const [revoked, setRevoked] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const [hostBlocked, setHostBlocked] = useState(false)
  const toRef = useRef<HTMLInputElement>(null)
  const formRef = useRef<HTMLFormElement>(null)
  const editorRef = useRef<HTMLDivElement>(null)
  const closeTimerRef = useRef<number | null>(null)
  const closingRef = useRef(false)

  useEffect(() => {
    let active = true

    loadGmailStatus()
      .then((status) => {
        if (!active) return
        setConnection(status.connected ? 'ready' : 'disconnected')
      })
      .catch(() => {
        if (active) setConnection('unavailable')
      })

    return () => {
      active = false
    }
  }, [])

  const beginClose = useCallback(() => {
    if (closingRef.current) return
    closingRef.current = true
    setIsClosing(true)

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
    closeTimerRef.current = window.setTimeout(onClose, reduceMotion ? 0 : 180)
  }, [onClose])

  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    toRef.current?.focus()

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') beginClose()
    }

    window.addEventListener('keydown', handleEscape)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleEscape)
      if (closeTimerRef.current !== null) window.clearTimeout(closeTimerRef.current)
    }
  }, [beginClose])

  // A short confirmation; the Sent button and read-only form keep the state.
  useEffect(() => {
    if (!sent) return
    setToast(true)
    const timer = window.setTimeout(() => setToast(false), 3200)
    return () => window.clearTimeout(timer)
  }, [sent])

  async function submitEmail(event: FormEvent) {
    event.preventDefault()

    /* Guards a double click and any repeat submit after a success. */
    if (sending || sent) return

    if (!to.trim()) {
      setError('A recipient is required.')
      return
    }

    if (!RECIPIENT_PATTERN.test(to.trim())) {
      setError('Enter one valid recipient address.')
      return
    }

    if (!subject.trim()) {
      setError('A subject is required.')
      return
    }

    const content = editorRef.current ? serializeEmail(editorRef.current) : { html: '', text: '' }

    if (!content.text) {
      setError('Write the message before sending.')
      return
    }

    setSending(true)
    setError('')

    try {
      setSent(await sendGmailEmail({ to: to.trim(), subject: subject.trim(), body: content.text, html: content.html }))
    } catch (cause) {
      const failure = cause instanceof GmailRequestError ? cause : null

      if (failure?.code === GMAIL_REAUTHORIZATION_REQUIRED) {
        setRevoked(true)
        setConnection('disconnected')
      }

      /* The written message stays in the form so nothing is lost. */
      setError(failure?.message ?? 'The email could not be sent.')
    } finally {
      setSending(false)
    }
  }

  // Ctrl+Enter (Cmd+Enter on Apple) sends from anywhere in the form, through
  // the same validated submit as the button.
  function sendOnShortcut(event: ReactKeyboardEvent<HTMLFormElement>) {
    if (event.key !== 'Enter' || !(event.ctrlKey || event.metaKey) || event.shiftKey || event.altKey) return
    event.preventDefault()
    if (connection === 'ready' && !sending && !sent) formRef.current?.requestSubmit()
  }

  const locked = connection !== 'ready' || Boolean(sent)

  return createPortal(
    <div className={`lead-modal-layer${isClosing ? ' is-closing' : ''}`} data-testid="send-email-modal">
      <button
        aria-label="Close Send Email"
        className="lead-modal-backdrop"
        onClick={beginClose}
        type="button"
      />
      <section
        aria-labelledby="send-email-title"
        aria-modal="true"
        className="lead-modal send-email-modal"
        role="dialog"
      >
        <header className="send-email-header">
          <div>
            <span>
              <Mail aria-hidden="true" size={15} />
              Send email
            </span>
            <h2 id="send-email-title">Send Email</h2>
            <p>Sends through the connected Gmail account.</p>
          </div>
          <button
            aria-label="Close Send Email"
            className="reminder-close"
            onClick={beginClose}
            type="button"
          >
            <X aria-hidden="true" size={18} />
          </button>
        </header>

        {connection === 'checking' ? (
          <p className="send-email-notice" role="status">
            Checking the Gmail connection...
          </p>
        ) : null}

        {connection === 'disconnected' ? (
          <div className="send-email-notice is-blocked" role="status">
            <strong>{revoked ? 'Gmail needs reconnecting' : 'Gmail is not connected'}</strong>
            <p>{revoked ? RECONNECT_MESSAGE : 'Connect Gmail in Profile before sending email from the CRM.'}</p>
            <button className="button button-primary" onClick={() => setHostBlocked(!connectGmail())} type="button">
              Connect Gmail
            </button>
            {hostBlocked ? <p className="send-email-host-note">{GMAIL_OAUTH_HOST_MESSAGE}</p> : null}
          </div>
        ) : null}

        {connection === 'unavailable' ? (
          <p className="send-email-notice is-blocked" role="alert">
            The Gmail connection could not be checked. Try again in a moment.
          </p>
        ) : null}

        <form className="send-email-form" onKeyDown={sendOnShortcut} onSubmit={submitEmail} ref={formRef}>
          <label>
            <span>To</span>
            <input
              onChange={(event) => setTo(event.target.value)}
              readOnly={locked}
              ref={toRef}
              type="email"
              value={to}
            />
          </label>

          <label>
            <span>Subject</span>
            <input
              maxLength={300}
              onChange={(event) => setSubject(event.target.value)}
              readOnly={locked}
              type="text"
              value={subject}
            />
          </label>

          <div className="send-email-body">
            <span id="send-email-body-label">Body</span>
            <EmailComposer
              editorRef={editorRef}
              labelledBy="send-email-body-label"
              placeholder={`Message ${lead.company || 'this prospect'}...`}
              readOnly={locked}
            />
          </div>

          {error && connection === 'ready' ? (
            <p className="send-email-result is-error" role="alert">
              {error}
            </p>
          ) : null}

          {connection === 'ready' ? (
            <div className="send-email-footer">
              {sent ? (
                <span />
              ) : (
                <span className="email-shortcut-hint">
                  <b>{IS_APPLE ? '\u2318' : 'Ctrl'}</b> + <b>Enter</b> to send
                </span>
              )}
              <div className="send-email-actions">
                <button
                  className="button send-email-cancel"
                  disabled={sending}
                  onClick={beginClose}
                  type="button"
                >
                  {sent ? 'Close' : 'Cancel'}
                </button>
                <button
                  aria-busy={sending || undefined}
                  aria-keyshortcuts="Control+Enter Meta+Enter"
                  className="button button-primary send-email-submit"
                  disabled={sending || Boolean(sent)}
                  type="submit"
                >
                  {sending ? (
                    <span aria-hidden="true" className="send-email-spinner" />
                  ) : (
                    <Send aria-hidden="true" size={16} />
                  )}
                  {sending ? 'Sending...' : sent ? 'Sent' : 'Send email'}
                </button>
              </div>
            </div>
          ) : null}
        </form>
      </section>

      {toast ? (
        <div className="lead-toast" role="status">
          <CheckCircle2 aria-hidden="true" size={16} />
          Email sent
        </div>
      ) : null}
    </div>,
    document.body,
  )
}
